
import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../layout/Layout";
import { Helmet } from "react-helmet-async";

/* ── decorative motif: thin horizontal rule with centre diamond ── */
const DividerDiamond = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <div className="flex-1 h-px bg-[#C8B89A]" />
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <rect x="5" y="0.5" width="6.5" height="6.5" rx="0.5" transform="rotate(45 5 0.5)" fill="#8B2020" />
    </svg>
    <div className="flex-1 h-px bg-[#C8B89A]" />
  </div>
);

/* ── animated checkmark seal ── */
const Seal = () => {
  const circleRef = useRef(null);
  const checkRef = useRef(null);

  useEffect(() => {
    const circle = circleRef.current;
    const check = checkRef.current;
    if (!circle || !check) return;

    const circleLen = circle.getTotalLength();
    circle.style.strokeDasharray = circleLen;
    circle.style.strokeDashoffset = circleLen;

    const checkLen = check.getTotalLength();
    check.style.strokeDasharray = checkLen;
    check.style.strokeDashoffset = checkLen;

    requestAnimationFrame(() => {
      circle.style.transition = "stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)";
      circle.style.strokeDashoffset = "0";
      setTimeout(() => {
        check.style.transition = "stroke-dashoffset 0.5s cubic-bezier(0.4,0,0.2,1)";
        check.style.strokeDashoffset = "0";
      }, 800);
    });
  }, []);

  return (
    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" aria-hidden>
      <circle cx="40" cy="40" r="36" stroke="#EDE8E2" strokeWidth="1.5" opacity="0.4" />
      <circle
        ref={circleRef}
        cx="40" cy="40" r="30"
        stroke="#8B2020"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      />
      <polyline
        ref={checkRef}
        points="24,40 35,52 56,28"
        stroke="#8B2020"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

/* ── order detail row ── */
const DetailRow = ({ label, value, mono = false }) => (
  <div className="flex justify-between items-baseline py-3 border-b border-[#C8B89A]/40 last:border-0">
    <span
      className="text-xs tracking-[0.12em] uppercase text-[#7A6A5A]"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {label}
    </span>
    <span
      className={`text-sm text-[#1A1A1A] ${mono ? "font-mono tracking-wider" : ""}`}
      style={{ fontFamily: mono ? "monospace" : "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
    >
      {value}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════
   MODAL — drop-in replacement for Checkout's inline modal
═══════════════════════════════════════════════════ */
export function ThankYouModal({ open, orderNo, emailSent, total, customerName, onClose }) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,10,5,0.72)", backdropFilter: "blur(4px)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-md rounded-none"
        style={{ background: "#F5F0EA", border: "1px solid #C8B89A" }}
      >
        {/* top maroon strip */}
        <div className="h-1 bg-[#8B2020] w-full" />

        <div className="px-8 pt-8 pb-10 text-center space-y-6">
          {/* seal */}
          <div className="flex justify-center">
            <Seal />
          </div>

          <div>
            <p
              className="text-[10px] tracking-[0.25em] uppercase text-[#8B2020] mb-2"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Order Confirmed
            </p>
            <h2
              className="text-2xl text-[#1A1A1A] leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, fontStyle: "italic" }}
            >
              {customerName ? `Thank you, ${customerName.split(" ")[0]}.` : "Thank you."}
            </h2>
            <p
              className="mt-2 text-xs tracking-wide text-[#7A6A5A] leading-relaxed"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Your order has been received and is being prepared with care.
            </p>
          </div>

          <DividerDiamond />

          {/* details */}
          <div className="text-left">
            {orderNo && <DetailRow label="Order Number" value={orderNo} mono />}
            {total && <DetailRow label="Amount Paid" value={total} />}
            <DetailRow
              label="Delivery"
              value="3 – 5 business days"
            />
            <DetailRow
              label="Confirmation"
              value={emailSent ? "Sent to your email" : "Arriving shortly"}
            />
          </div>

          <DividerDiamond />

          {/* CTA row */}
          <div className="flex gap-3 pt-2">
            <Link
              to="/myorder"
              onClick={onClose}
              className="flex-1 text-center py-3 text-xs tracking-[0.15em] uppercase text-white bg-[#1A1A1A] hover:bg-[#8B2020] transition-colors duration-300"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              View Orders
            </Link>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-xs tracking-[0.15em] uppercase text-[#1A1A1A] border border-[#C8B89A] hover:border-[#8B2020] hover:text-[#8B2020] transition-colors duration-300"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Continue Shopping
            </button>
          </div>
        </div>

        {/* close X */}
        <button
          onClick={onClose}
          className="absolute top-4 right-5 text-[#7A6A5A] hover:text-[#1A1A1A] text-lg leading-none transition-colors"
          aria-label="Close"
        >
          ×
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   FULL PAGE — route: /thank-you
   Receives: ?order=SM-XXX&email=a@b.com&items=2&total=₹6,600.00&name=Arjun
═══════════════════════════════════════════════════ */
function ThankYou() {
  const [params] = useSearchParams();
  const orderNo   = params.get("order")  || "";
  const email     = params.get("email")  || "";
  const itemCount = params.get("items")  || "";
  const total     = params.get("total")  || "";
  const name      = params.get("name")   || "";
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Order Confirmed — Shahu Mumbai</title>
        <meta name="robots" content="noindex,nofollow" />
        <link
          href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,600&family=Jost:wght@300;400;500&display=swap"
          rel="stylesheet"
        />
      </Helmet>

      {/* page background */}
      <div
        className="min-h-screen"
        style={{ background: "#EDE8E2" }}
      >
        {/* thin full-width maroon bar */}
        <div className="h-px w-full bg-[#8B2020] opacity-60" />

        <div
          className="max-w-lg mx-auto px-6 py-16 flex flex-col items-center text-center"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(12px)",
            transition: "opacity 0.7s ease, transform 0.7s ease",
          }}
        >
          {/* ── eyebrow ── */}
          <p
            className="text-[10px] tracking-[0.3em] uppercase text-[#8B2020] mb-6"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            Shahu Mumbai
          </p>

          {/* ── seal ── */}
          <Seal />

          {/* ── headline ── */}
          <div className="mt-6 mb-2 space-y-1">
            <h1
              className="text-4xl md:text-5xl text-[#1A1A1A] leading-tight"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                fontWeight: 600,
                fontStyle: "italic",
                letterSpacing: "-0.01em",
              }}
            >
              {name ? `Thank you, ${name.split(" ")[0]}.` : "Order Confirmed."}
            </h1>
            <p
              className="text-sm text-[#7A6A5A] tracking-wide mt-3 leading-relaxed"
              style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
            >
              Every piece from Shahu is crafted to last.
              <br />
              Yours is already being prepared.
            </p>
          </div>

          {/* ── divider ── */}
          <DividerDiamond className="w-full my-8" />

          {/* ── order card ── */}
          <div
            className="w-full text-left mb-8"
            style={{
              background: "#F5F0EA",
              border: "1px solid #C8B89A",
              padding: "2rem",
            }}
          >
            <p
              className="text-[9px] tracking-[0.25em] uppercase text-[#8B2020] mb-4"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Order Details
            </p>

            {orderNo   && <DetailRow label="Order Number"  value={orderNo}                    mono />}
            {total     && <DetailRow label="Amount Paid"   value={total}                               />}
            {itemCount && <DetailRow label="Items"         value={`${itemCount} piece${itemCount === "1" ? "" : "s"}`} />}
            {email     && <DetailRow label="Email"         value={email}                               />}
            <DetailRow   label="Estimated Delivery" value="3 – 5 business days"                      />
            <DetailRow   label="Confirmation"       value={email ? "Sent to your email" : "Arriving shortly"} />
          </div>

          {/* ── what happens next ── */}
          {/* <div className="w-full text-left mb-8 space-y-4">
            {[
              { step: "01", title: "Preparation",  body: "Your order is being reviewed and carefully packaged by our atelier team." },
              { step: "02", title: "Dispatch",      body: "You will receive a tracking link within 24–48 hours via email." },
              { step: "03", title: "Delivery",      body: "Your piece arrives within 3–5 business days, wrapped in our signature box." },
            ].map(({ step, title, body }) => (
              <div key={step} className="flex gap-4">
                <span
                  className="text-[10px] text-[#8B2020] mt-1 shrink-0 tabular-nums"
                  style={{ fontFamily: "'Jost', sans-serif", letterSpacing: "0.1em" }}
                >
                  {step}
                </span>
                <div>
                  <p
                    className="text-xs uppercase tracking-[0.12em] text-[#1A1A1A] mb-0.5"
                    style={{ fontFamily: "'Jost', sans-serif", fontWeight: 500 }}
                  >
                    {title}
                  </p>
                  <p
                    className="text-xs text-[#7A6A5A] leading-relaxed"
                    style={{ fontFamily: "'Jost', sans-serif", fontWeight: 300 }}
                  >
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div> */}

          <DividerDiamond className="w-full mb-8" />

          {/* ── CTAs ── */}
          <div className="w-full flex flex-col sm:flex-row gap-3">
            {/* <Link
              to="/myorder"
              className="flex-1 text-center py-4 text-xs tracking-[0.2em] uppercase text-white bg-[#1A1A1A] hover:bg-[#8B2020] transition-colors duration-400"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Track My Order
            </Link> */}
            <Link
              to="/products"
              className="flex-1 text-center py-4 text-xs tracking-[0.2em] uppercase text-[#1A1A1A] border border-[#C8B89A] hover:border-[#8B2020] hover:text-[#8B2020] transition-colors duration-400"
              style={{ fontFamily: "'Jost', sans-serif" }}
            >
              Continue Shopping
            </Link>
          </div>

          {/* ── footer whisper ── */}
          <p
            className="mt-12 text-[10px] tracking-[0.15em] uppercase text-[#C8B89A]"
            style={{ fontFamily: "'Jost', sans-serif" }}
          >
            For queries — care@shahumumbai.com
          </p>
        </div>

        <div className="h-px w-full bg-[#8B2020] opacity-30" />
      </div>
    </Layout>
  );
}

export default ThankYou;
